"use client"

import { useState } from "react"
import Link from "next/link"
import { Loader2, ChevronLeft } from "lucide-react"
import { Logo } from "@/components/logo"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { createClient } from "@/lib/supabase/client"
import posthog from "posthog-js"

export function LoginForm({ returnTo, error: serverError }) {
  const [email, setEmail] = useState("")
  const [loading, setLoading] = useState(false)
  const [magicLinkSent, setMagicLinkSent] = useState(false)
  const [error, setError] = useState(serverError === "auth" ? "Authentication failed. Please try again." : null)
  const [honeypot, setHoneypot] = useState("")

  const supabase = createClient()
  const isCheckout = returnTo?.startsWith("/checkout")
  const callbackUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/auth/callback?next=${encodeURIComponent(returnTo || "/app")}`

  async function handleGoogleLogin() {
    setLoading(true)
    setError(null)

    posthog.capture("signed_in", { method: "google" })

    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: callbackUrl,
      },
    })

    if (error) {
      posthog.captureException(error, { method: "google" })
      setError(error.message)
      setLoading(false)
    }
  }

  async function handleMagicLink(e) {
    e.preventDefault()
    if (!email.trim()) return

    // Bot detection: honeypot field filled
    if (honeypot) {
      setMagicLinkSent(true) // fake success so bots think it worked
      return
    }

    setLoading(true)
    setError(null)

    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: {
        emailRedirectTo: callbackUrl,
      },
    })

    setLoading(false)

    if (error) {
      posthog.captureException(error, { method: "magic_link" })
      setError(error.message)
    } else {
      posthog.capture("signed_in", { method: "magic_link", email: email.trim() })
      setMagicLinkSent(true)
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      <div className="mb-8">
        <Logo className="h-8 w-auto" />
      </div>

      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-xl">
            {magicLinkSent ? "Check your email ✉️" : isCheckout ? "Sign in to continue" : `Welcome \u{1F44B}`}
          </CardTitle>
          {magicLinkSent ? (
            <>
              <CardDescription>We just sent a magic link to:</CardDescription>
              <div className="mt-2 flex justify-center">
                <span className="rounded-md bg-muted px-3 py-1.5 text-sm font-medium text-muted-foreground">{email}</span>
              </div>
            </>
          ) : (
            <CardDescription>
              {isCheckout
                ? "Create an account or sign in to complete your purchase"
                : "Sign in to start practicing"}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          {magicLinkSent ? (
            <div className="flex flex-col items-center gap-4">
              <p className="text-center text-sm text-muted-foreground">You can close this tab.</p>
              <button
                className="text-sm text-muted-foreground underline-offset-4 hover:underline"
                onClick={() => {
                  setMagicLinkSent(false)
                  setEmail("")
                }}
              >
                Use a different email
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <Button
                variant="outline"
                className="w-full"
                onClick={handleGoogleLogin}
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                )}
                Continue with Google
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">
                    or
                  </span>
                </div>
              </div>

              <form onSubmit={handleMagicLink} className="flex flex-col gap-3">
                {/* Honeypot - hidden from real users, bots auto-fill it */}
                <div aria-hidden="true" className="absolute -left-[9999px] -top-[9999px]">
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
                <Button type="submit" disabled={loading || !email.trim()}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Send magic link
                </Button>
              </form>

              {error && (
                <p className="text-center text-sm text-destructive">{error}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {!magicLinkSent && (
        <p className="mt-6 text-center text-xs text-muted-foreground">
          By continuing, you agree to our{" "}
          <Link href="/terms" className="font-semibold">Terms of Use</Link>
          {" "}and{" "}
          <Link href="/privacy" className="font-semibold">Privacy Policy</Link>.
        </p>
      )}
    </div>
  )
}
